/**
 * The dashboard's BOOKINGS tab: every offer / booking a Pro page has received, from
 * `GET pro/offers/incoming` (ProOfferDto). Pure, so the filters and the order are tested apart
 * from the panel.
 *
 * Offer status (ProBookingService): sent · countered · accepted · confirmed · declined · expired
 * (canceled is kept for older rows). Once `confirmed` the booking's escrow state says the rest:
 * held · flagged · released · refunded · resolved · chargeback · charged_back.
 */
import { offerSessions } from '../../utils/proBookingTime';

const GREEN = '#22C55E';
const YELLOW = '#FACC15';
const RED = '#DC2626';
const CHIP = '#F2F2F2';
const GREY = '#E5E7EB';
const MUTED = '#6B7280';

/** status key → [label, background, text]. */
export const STAGE_PILL = {
  new: ['NEW OFFER', '#000', '#fff'],
  waiting: ['WAITING ON CLIENT', CHIP, '#000'],
  countered: ['COUNTERED', YELLOW, '#000'],
  accepted: ['AGREED · AWAITING PAYMENT', '#00FFFF', '#000'],
  booked: ['PAID · BOOKED', GREEN, '#fff'],
  disputed: ['DISPUTED', RED, '#fff'],
  released: ['PAID OUT', '#DCFCE7', '#166534'],
  refunded: ['REFUNDED', GREY, MUTED],
  resolved: ['SETTLED', GREY, '#000'],
  charged_back: ['CHARGED BACK', GREY, RED],
  declined: ['DECLINED', GREY, MUTED],
  expired: ['EXPIRED', GREY, MUTED],
  canceled: ['CANCELED', GREY, MUTED],
};

/** Where an offer stands, as one key of STAGE_PILL. */
export function stageOf(o) {
  switch (o?.status) {
    case 'sent': return o.currentTurn === 'buyer' ? 'waiting' : 'new';
    case 'countered': return o.currentTurn === 'owner' ? 'countered' : 'waiting';
    case 'accepted': return 'accepted';
    case 'confirmed': {
      const e = o.booking?.escrowState;
      if (o.booking?.hasOpenFlag || e === 'flagged' || e === 'chargeback') return 'disputed';
      if (e === 'released') return 'released';
      if (e === 'refunded') return 'refunded';
      if (e === 'resolved') return 'resolved';
      if (e === 'charged_back') return 'charged_back';
      return 'booked';
    }
    case 'declined': return 'declined';
    case 'expired': return 'expired';
    case 'canceled': case 'cancelled': return 'canceled';
    default: return o?.status || 'new';
  }
}

const CLOSED = new Set(['declined', 'expired', 'canceled', 'refunded', 'charged_back']);

/** The owner's move: an offer waiting on them, or a dispute to answer. */
export const needsAction = (o) => {
  const s = stageOf(o);
  return s === 'new' || s === 'countered' || s === 'disputed';
};

const startOf = (s) => (s.startsAt ? new Date(s.startsAt) : s.eventDate ? new Date(s.eventDate) : null);
const endOf = (s) => (s.endsAt ? new Date(s.endsAt) : startOf(s));

/** The first session that hasn't ended yet, or null. */
export function nextSession(o, now = new Date()) {
  return offerSessions(o).find((s) => { const e = endOf(s); return e && e >= now; }) || null;
}

/**
 * past: closed, or every session has ended. upcoming: still open and a session is still ahead
 * (an open offer with no date at all counts — it isn't over).
 */
export function isPast(o, now = new Date()) {
  if (CLOSED.has(stageOf(o))) return true;
  const sessions = offerSessions(o);
  if (sessions.length === 0) return false;
  return !nextSession(o, now);
}

export const FILTERS = [['upcoming', 'UPCOMING'], ['action', 'NEEDS ACTION'], ['past', 'PAST'], ['all', 'ALL']];

export function matches(filter, o, now = new Date()) {
  if (filter === 'upcoming') return !isPast(o, now);
  if (filter === 'action') return needsAction(o);
  if (filter === 'past') return isPast(o, now);
  return true;
}

/**
 * By next session: what's coming soonest first; then open offers with no date (newest first);
 * then the past, most recent first.
 */
export function sortBookings(list, now = new Date()) {
  const key = (o) => {
    const next = nextSession(o, now);
    if (next && !isPast(o, now)) return [0, startOf(next).getTime()];
    const sessions = offerSessions(o);
    if (sessions.length === 0 && !isPast(o, now)) return [1, -new Date(o.updatedAt || o.createdAt || 0).getTime()];
    const last = sessions.length ? startOf(sessions[sessions.length - 1]) : new Date(o.updatedAt || o.createdAt || 0);
    return [2, -last.getTime()];
  };
  return [...list].map((o) => [key(o), o]).sort(([a], [b]) => a[0] - b[0] || a[1] - b[1]).map(([, o]) => o);
}

/** The filtered, ordered rows plus the count behind each chip. */
export function bookingsView(list, filter, now = new Date()) {
  const counts = Object.fromEntries(FILTERS.map(([k]) => [k, list.filter((o) => matches(k, o, now)).length]));
  return { rows: sortBookings(list.filter((o) => matches(filter, o, now)), now), counts };
}
